'use client'

/**
 * ArticleView — the shared three-column reader (client half).
 *
 * Used by /tutorials/[slug] (via the thin `TutorialView` wrapper) and by
 * /creating-a-scene/[slug] (every scene is on the chapter model since the
 * CAS migration; the media-tab SceneView was removed in PR-9). The server
 * half of both pages is `src/lib/content/article-page.ts`.
 *
 * Renders the three-column editorial layout from the design concept,
 * wired to real Sanity article data passed down from the server page:
 *   - left rail  · featured image (opens full-screen) + scroll-spy table of
 *                  contents + reading progress
 *   - center     · article (chapter sections — prose + step grids +
 *                  video + bonus photos + annotated scenes — what's
 *                  next, comments)
 *   - right rail · Tools (Bookmark / Like / Share) · Safety notice ·
 *                  Rope Needed · Credits · Prerequisites ·
 *                  More Like This
 *
 * On mobile both rails collapse and become a right-edge pull-out
 * drawer (TOC + Tools); Safety / Rope / Credits / Prerequisites are rolled
 * into the article body below the header so they aren't lost. The featured
 * image renders inline in the header whenever the left rail is not showing
 * (below `lg`, or the rail is collapsed on desktop).
 *
 * Palette is the site's own — the concept's rust accent maps onto
 * `--rope-deep`, the warm paper onto a tint of `--rope-light`.
 *
 * Access control:
 *   The page resolves `isGated` / `hasAccess` / `isAuthenticated`
 *   server-side. When the tutorial is gated and the user lacks
 *   access, the body sections (chapters, related, safety, rope,
 *   prerequisites) are replaced with a <ContentGate />
 *   paywall — the title, cover, excerpt, what's-next, related
 *   content, and comments stay visible.
 *
 * Optional slots (flogger migration PR-2c; off unless a page passes them):
 *   `extraSections` (non-chapter sections before / after the chapters, with
 *   TOC entries and `#id` anchors) and `headerAction` (an action in the
 *   header chip row). See `ArticleExtraSection`. PR-3 adds `headerEyebrow`
 *   (a line above the title) and `headerMeta` (under the byline).
 *
 * Engagement:
 *   Tools (like / bookmark) write to `likes` / `bookmarks` keyed off
 *   the stable `contentId` (uuid from content_index) resolved by the
 *   server. Comments read the same id.
 */

import { useCallback, useEffect, useMemo, useRef, useState, forwardRef, Fragment } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { Link } from '@/i18n/navigation'
import {
  Clock,
  AlertTriangle,
  PanelRightOpen,
  X,
  Lock,
  Camera,
  Images,
  AlignLeft,
  type LucideIcon,
} from 'lucide-react'
import {
  PortableTextRenderer,
  ImageLayoutContext,
  type ImageLayoutConfig,
} from '@/components/content/portable-text-renderer'
import {
  StepViewProvider,
  type StepViewMode,
  type StepViewOptions,
} from '@/components/content/step-view-preference'
import {
  ChapterStepProgress,
  StepProgressChapter,
  StepProgressOff,
  StepProgressProvider,
  StepProgressResumeButton,
  useStepTrackingPreference,
} from '@/components/content/step-progress'
import { buildChapterStepIndex, type ChapterSteps } from '@/lib/content/step-progress'
import { useCollapsibleRail } from '@/hooks/useCollapsibleRail'
import { RailCollapseButton, RailReopenHandle } from '@/components/content/rail-collapse'
import { authorDisplayName } from '@/lib/author'
import { optimizeSanityImageUrl } from '@/lib/sanity/client'
import dynamic from 'next/dynamic'
import { slugifyAnchor } from '@/lib/content/slugify-anchor'
import { ContentGate } from '@/components/content/content-gate'
import { SquareBlurImage } from '@/components/content/square-blur-image'
import { ToolsRow } from '@/components/content/tools-row'
import { ContentRefListItem } from '@/components/content/content-ref-list-item'
import { Lightbox } from '@/components/content/lightbox'
import { CreditsBlock, hasCredits, type SceneCredits } from '@/components/content/credits-block'
import { resolveSectionAnchors } from '@/lib/content/article-anchors'
import {
  normalizeExtraSections,
  orderSectionsWithExtras,
  type ExtraSectionPlacement,
} from '@/lib/content/article-extra-sections'

// Comments live below the fold; lazy-load (client-only) so their JS and markup
// stay out of the initial payload — a first-paint win on this content page
// without changing what a reader sees (the section is well below the fold).
const Comments = dynamic(
  () => import('@/components/content/comments').then((m) => m.Comments),
  { ssr: false },
)

// ─── Data shape (built by the server page) ───────────────────────

/** A content card target — thumbnail + title + optional link. Shared
 *  by the Related techniques and What's next sections. */
export interface LinkedCard {
  id: string
  title: string
  href: string | null
  thumbnailUrl: string | null
  /** `cards.chip.*` key for the content type ("flogger", "scene", …). When
   *  set, the localized label renders as a chip on the lower-right of the
   *  card thumbnail. */
  typeKey?: string | null
}

// Raw Portable Text array. Loose-typed because PortableTextRenderer
// already does its own per-block narrowing; SectionShell just forwards
// the array through.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SectionIntro = any[] | null

export type Section =
  | {
      kind: 'chapter'
      id: string
      label: string
      /** The chapter's active-locale Portable Text content (prose +
       *  stepByStepGrid / videoBlock / bonusPhotosBlock), resolved
       *  server-side. Rendered through PortableTextRenderer, which owns
       *  the step/bonus lightbox + inline chapter render. */
      content: SectionIntro
      /** Extra legacy `#fragment` ids other in-page links target (from the
       *  chapter's `anchorIds`). The section is already reachable by its
       *  title slug; these restore WordPress anchors that differ from the
       *  title so old "jump to this section" links resolve. */
      anchorIds?: string[]
      /** Converted-CAS chapters on a non-English page: `{_key, t}` of the
       *  chapter's English text blocks (GROQ `enTextKeys`), so copied-but-
       *  untranslated English renders lang="en" (plan D11 / C8). Absent on
       *  tutorials. */
      enTextKeys?: { _key?: string | null; t?: string | null }[]
      /** "en" when (converted-CAS chapters only) the title fell back to
       *  English on a non-English page — marks the heading lang="en". */
      labelLang?: string
    }
  | { kind: 'whatsNext'; id: string; label: string; items: LinkedCard[] }
  | { kind: 'comments'; id: string; label: string }

/**
 * A page section that is not a chapter (flogger PR-2c slot; PR-3 passes the
 * Rope section and "Where you'll tie it"). ArticleView renders the `<h2>`
 * (`tocLabel`) and a TOC entry, and wraps `content` like a chapter. It is
 * never counted as a chapter: no step progress (a step block inside gets no
 * checkbox) and no chapter count.
 */
export interface ArticleExtraSection {
  /** Stable, slug-safe anchor, e.g. `rope`: the section's DOM `id` and its
   *  `#hash` (TOC clicks write it; deep links resolve it). It wins over a
   *  chapter title slug that happens to equal it. Slugified if it isn't
   *  slug-safe; dropped if empty, `overview`, or a repeat of an earlier one. */
  id: string
  /** Heading and TOC label (already localized). */
  tocLabel: string
  /** Just before the first chapter, or just after the last one (before
   *  What's next / Comments). Each group keeps the array's order. */
  placement: ExtraSectionPlacement
  /** The section body, below the heading. */
  content: React.ReactNode
  /** Render it on a gated page for a reader without access (outside the
   *  paywall). Default false: like a chapter it is locked (TOC lock icon,
   *  click and `#hash` go to the paywall). */
  showWhenGated?: boolean
}

export interface ArticleData {
  title: string
  excerpt: string
  /** Cover image — the featured-image hero at the top of the page. */
  coverImageUrl: string | null
  /** Alt text of the cover image asset (Sanity `asset.altText`); the
   *  featured image falls back to the title when it's empty. */
  coverImageAlt?: string | null
  /** Full Portable Text description, rendered above the video. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body: any[] | null
  /** BCP-47 lang for the body + promoted chapter sections when they fell back
   *  to English on a non-English page (WCAG 3.1.2); null = no marking. The
   *  whole doc is translated as a unit, so one tag covers body and chapters. */
  bodyLang?: string | null
  difficulty: string | null
  author: { display_name?: string | null } | string | null
  /** Stable content_index.id (uuid) for the comments / likes /
   *  bookmarks join key, and the signed-in user — both resolved
   *  server-side. */
  contentId: string | null
  userId: string | null
  /** Article slug — used by Share fallback when JS-built URL isn't
   *  the canonical one. */
  slug: string
  /** Absolute URL for share targets / canonical references
   *  (`/tutorials/<slug>`, `/creating-a-scene/<slug>`). */
  shareUrl: string
  /** Access control — when `isGated && !hasAccess`, the body sections
   *  collapse into a paywall. */
  isGated: boolean
  /** When false, the comments section is omitted entirely. */
  commentsEnabled: boolean
  hasAccess: boolean
  isAuthenticated: boolean
  // Rope Needed — localePortableText, flattened to the active-locale
  // block array server-side (EN fallback). Rendered via PortableTextRenderer.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ropeNeeded: any[]
  // body is localePortableText flattened to the active-locale block array
  // server-side (EN fallback), rendered via PortableTextRenderer so links /
  // lists / bold authored in the safety notice survive.
  safetyItems: { title: string | null; body: unknown[] }[]
  prerequisites: { id: string; title: string; slug: string; href: string; thumbnailUrl: string | null }[]
  /** "Other tutorials in the same family" — rendered in the desktop
   *  right rail underneath Prerequisites, with the same card layout. */
  relatedTechniques: { id: string; title: string; slug: string; thumbnailUrl: string | null }[]
  /** Credits (model / rigger / photographer / location) — scenes and
   *  tutorials. Public attribution — shown to every viewer, including locked
   *  ones (plan D8). */
  credits?: SceneCredits | null
  /** Heading + TOC label for the credits block. Defaults to "Scene Credits"
   *  (scenes); tutorials pass "Credits". */
  creditsLabel?: string
  sections: Section[]
}

/** @deprecated name — the tutorial page's data is the shared article shape. */
export type TutorialData = ArticleData

// Display headings use the site sans (Atkinson Hyperlegible) — same as
// the body. (Previously a Georgia editorial-serif stack.)
const HEADING_FONT = 'var(--font-sans)'

// The title + description block at the top isn't a media section, but
// it gets a synthetic TOC entry so it's reachable from the rail.
const OVERVIEW_ID = '__overview'
/** Step progress off: one shared empty chapter list (stable reference). */
const NO_CHAPTERS: ChapterSteps[] = []

// Auxiliary anchors — these blocks live in the right rail on desktop,
// but are rendered inline in the main content column on mobile (where
// the rails collapse out) and surface as extra TOC entries inside the
// mobile drawer. The IDs are referenced by both the inline sections
// (via ref) and the mobile-only TOC entries (via scrollToSection).
const AUX_SAFETY_ID = '__safety'
const AUX_ROPE_ID = '__rope'
const AUX_PREREQ_ID = '__prerequisites'
// Credits (scenes + tutorials). Public attribution, so never locked (D8).
const AUX_CREDITS_ID = '__credits'

/**
 * Resolve a URL-hash fragment to an on-page element, tolerating case.
 * Legacy WordPress in-content anchor links preserved the heading's original
 * capitalization (e.g. `#Converting-a-Double-Column-into-a-Load-Bearing-
 * Double-Column`), but chapters now render a lowercase slug id, so an exact
 * `#id` match fails and the browser scrolls nowhere. Try the exact id first,
 * then a case-insensitive scan of every element with an id (covers both the
 * lowercase chapter slugs and any legacy `anchorIds` spans).
 */
function findElementByCaseInsensitiveId(rawId: string): HTMLElement | null {
  if (typeof document === 'undefined' || !rawId) return null
  const exact = document.getElementById(rawId)
  if (exact) return exact
  const lower = rawId.toLowerCase()
  for (const node of document.querySelectorAll<HTMLElement>('[id]')) {
    if (node.id.toLowerCase() === lower) return node
  }
  return null
}

/**
 * TOC entry type icon. Sections are classified by what their content holds
 * so the left rail marks each entry the way the old site did — a camera on
 * the video section, a photos icon on step/photo sections, and a text icon
 * on written sections. Overview / What's next / Comments / aux entries are
 * textual and default to the text icon.
 */
type TocIconKind = 'video' | 'photos' | 'text'

// Portable-text block _types that make a chapter a "photos" (visual) section.
// `videoBlock` is checked first and wins (→ 'video').
const PHOTO_BLOCK_TYPES = [
  'stepByStepGrid',
  'bonusPhotosBlock',
  'imageGrid',
  'annotatedImage',
  'enhancedImage',
  'image',
]

const TOC_ICONS: Record<TocIconKind, LucideIcon> = {
  video: Camera,
  photos: Images,
  text: AlignLeft,
}

/**
 * Classify a section for its TOC icon. Only chapters carry media; every
 * other section kind (whatsNext / comments) reads as text. A compact JSON
 * scan of the chapter's resolved content catches media nested inside
 * accordions/chapters too.
 */
function sectionIconKind(section: Section): TocIconKind {
  if (section.kind !== 'chapter') return 'text'
  const json = JSON.stringify(section.content ?? '')
  if (json.includes('"_type":"videoBlock"')) return 'video'
  if (PHOTO_BLOCK_TYPES.some((t) => json.includes(`"_type":"${t}"`))) return 'photos'
  return 'text'
}

/** localStorage keys for the two collapsible rails. Scenes share the
 *  tutorial keys (and the tutorialToc / tutorialTools account prefs — plan
 *  D15), so one reader choice covers both. */
const DEFAULT_RAIL_KEYS = {
  toc: 'theduchy.reader-rail.tutorial.toc',
  tools: 'theduchy.reader-rail.tutorial.tools',
} as const

export function ArticleView({
  data,
  imageLayoutConfig,
  stepViewDefault,
  stepViewAuthed,
  stepViewDefaultMode,
  stepViewOptions,
  stepProgress,
  stepTrackingDefault,
  extraSections,
  headerAction,
  headerEyebrow,
  headerMeta,
  tocInitialCollapsed,
  toolsInitialCollapsed,
  railKeys = DEFAULT_RAIL_KEYS,
}: {
  data: ArticleData
  imageLayoutConfig: ImageLayoutConfig
  /** Reader's saved step-view preference (null = unset → localStorage wins). */
  stepViewDefault: StepViewMode | null
  /** Whether the reader is signed in (controls persisting the pill choice). */
  stepViewAuthed: boolean
  /**
   * Starting step view for a reader with no saved choice; never persisted.
   * Omitted → 'list' (tutorials, scenes). Flogger components pass 'carousel'
   * from PR-3 (design doc D8).
   */
  stepViewDefaultMode?: StepViewMode
  /** Opt-in step-view features (matching-number prefix strip,
   *  carousel keys). Omitted → all off. */
  stepViewOptions?: StepViewOptions
  /**
   * Step progress (flogger PR-2b): "Mark step N done" checkboxes, per-chapter
   * "x of n done" under each chapter heading, and "Resume · N/M" in the
   * header. `docId` is the Sanity `_id`; done steps persist in localStorage
   * `step-progress:<docId>`. This only makes progress AVAILABLE: a
   * "Track my steps" switch appears next to the step-view picker and the
   * reader's choice decides whether progress shows (off by default; owner
   * decision 2026-11-04). Omitted → no switch, no checkbox, no count, no
   * Resume, no storage access (tutorials, scenes). PR-3 passes it for the
   * flogger component types only.
   */
  stepProgress?: { docId: string } | null
  /** Account "Track my steps" choice (`profiles.step_tracking_enabled`, read
   *  on the server). Null / omitted → the browser's saved choice, else off. */
  stepTrackingDefault?: boolean | null
  /**
   * Extra page sections that are not chapters (flogger PR-2c; PR-3 passes the
   * Rope section and "Where you'll tie it"), each with a heading, a TOC entry
   * in page order, an `#id` anchor, scroll-spy and reading progress. See
   * `ArticleExtraSection`. Omitted / empty → the page is unchanged.
   */
  extraSections?: readonly ArticleExtraSection[] | null
  /**
   * An action in the header chip row, after the difficulty / Members chips
   * and before Resume (flogger PR-2c; PR-3 passes the Notation guide button).
   * The row wraps on narrow screens. Omitted → nothing rendered.
   */
  headerAction?: React.ReactNode
  /**
   * A small line above the title (flogger PR-3 passes the "← Flogger Forge /
   * <component type>" breadcrumb nav). Omitted → nothing rendered.
   */
  headerEyebrow?: React.ReactNode
  /**
   * Extra header metadata under the byline (flogger PR-3 passes the
   * published date as a `<time>`). Omitted → nothing rendered.
   */
  headerMeta?: React.ReactNode
  /** Account preference: start the Contents (TOC) rail collapsed / no preference. */
  tocInitialCollapsed?: boolean | null
  /** Account preference: start the Tools rail collapsed / no preference. */
  toolsInitialCollapsed?: boolean | null
  /** localStorage keys for the rails' per-device collapse choice. */
  railKeys?: { toc: string; tools: string }
}) {
  // Root-scoped: hero chips use filters.* / blogPost.byAuthor.
  const t = useTranslations()
  // When the tutorial is gated and the viewer lacks access, the body
  // sections (video / steps / photos / related techniques / safety /
  // rope / prerequisites) collapse into a paywall. Comments,
  // what's-next, and the page chrome stay visible regardless.
  const gated = data.isGated && !data.hasAccess

  // The sections actually rendered in the main column. When gated,
  // ALL sections collapse — including what's-next and comments —
  // because the user explicitly wants the paywall to cover the
  // whole reading experience, not just the media body. The locked
  // section ids still appear in the TOC so users see the full
  // breadth of what's behind the paywall.
  const visibleSections: Section[] = useMemo(
    () => (gated ? [] : data.sections),
    [gated, data.sections],
  )

  // The set of section ids that are present in the TOC but NOT
  // rendered in the body (gated → behind the paywall). Tapping any
  // of these scrolls to the paywall block instead of nowhere, and
  // they render with a lock icon in the rail so users can see the
  // breadth of the locked content without being misled into thinking
  // the link is "broken".
  const lockedSectionIds = new Set<string>(
    gated
      ? data.sections
          .filter((s) => !visibleSections.includes(s))
          .map((s) => s.id)
      : [],
  )

  // Extra (non-chapter) sections (PR-2c), keyed `__extra:<anchor>` so they
  // never collide with a chapter id, and placed before / after the chapters.
  // On a gated page they are locked like chapters unless `showWhenGated`.
  // With none, `orderedSections` is the sections, so the page is unchanged.
  const extras = useMemo(() => normalizeExtraSections(extraSections), [extraSections])
  const orderedSections = useMemo(
    () => orderSectionsWithExtras(data.sections, extras),
    [data.sections, extras],
  )
  for (const e of extras) {
    if (gated && !e.extra.showWhenGated) lockedSectionIds.add(e.key)
  }
  // What the main column renders, in page order (refs + scroll-spy).
  const renderedSections = useMemo(
    () =>
      orderedSections.filter((entry) =>
        entry.type === 'section'
          ? visibleSections.includes(entry.section)
          : !gated || entry.extra.extra.showWhenGated === true,
      ),
    [orderedSections, visibleSections, gated],
  )

  // Step progress (PR-2b): tracked steps per RENDERED chapter (none when
  // gated, so a locked page shows no counts and no Resume). Off → no scan.
  // The locale picks the shown copy of nested localePortableText maps (an
  // accordion panel's content), as AccordionBlock does, so a grid inside one
  // counts once rather than once per translation.
  const pageLocale = useLocale()
  const stepProgressDocId = stepProgress?.docId || null
  // The reader's "Track my steps" choice; progress shows only when the page
  // offers it AND this is on. Off by default for everyone.
  const [stepTracking, setStepTracking] = useStepTrackingPreference({
    available: stepProgressDocId !== null,
    initialTracking: stepTrackingDefault ?? null,
    authed: stepViewAuthed,
  })
  const stepProgressOn = stepProgressDocId !== null && stepTracking
  const progressChapters = useMemo<ChapterSteps[]>(
    () => (stepProgressOn ? buildChapterStepIndex(visibleSections, pageLocale) : NO_CHAPTERS),
    [stepProgressOn, visibleSections, pageLocale],
  )

  const [activeSection, setActiveSection] = useState<string>(OVERVIEW_ID)
  const [progress, setProgress] = useState(0)
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({})
  // Paywall block ref — used as the scroll target for any TOC entry
  // whose section is gated (and therefore not rendered).
  const paywallRef = useRef<HTMLDivElement | null>(null)

  // TOC entries — synthetic "Overview" first, then one per declared
  // section (even when gated, so the user sees the full breadth of
  // what's locked).
  // Per-section TOC icon kind, memoized so the JSON content scan doesn't
  // re-run on every scroll-spy re-render.
  const sectionIconKinds = useMemo<Record<string, TocIconKind>>(() => {
    const m: Record<string, TocIconKind> = {}
    for (const s of data.sections) m[s.id] = sectionIconKind(s)
    return m
  }, [data.sections])

  type TocEntry = { id: string; label: string; count: number | null; iconKind: TocIconKind }
  const tocEntries: TocEntry[] = [
    { id: OVERVIEW_ID, label: t('articleView.overview'), count: null, iconKind: 'text' },
    ...orderedSections.map((entry): TocEntry =>
      entry.type === 'section'
        ? {
            id: entry.section.id,
            label: entry.section.label,
            count: null,
            iconKind: sectionIconKinds[entry.section.id] ?? 'text',
          }
        : { id: entry.extra.key, label: entry.extra.extra.tocLabel, count: null, iconKind: 'text' },
    ),
  ]

  // URL-friendly anchor per TOC entry (slug of the label, de-duped) so
  // /tutorials/<slug>#<anchor> deep-links straight to a chapter. Scroll-spy
  // + refs still key off the opaque section id; these bridge id ↔ the hash.
  // Explicit chapter anchors (`anchorId` / legacy `anchorIds`) are reserved
  // first, so a derived title slug never duplicates another section's
  // explicit anchor, and an explicit anchor equal to the section's own slug
  // is not emitted twice (no duplicate DOM ids — plan O2 / D20).
  // Extra sections go first with their `id` as an explicit anchor, so each
  // keeps exactly its `id` (a chapter whose slug equals it moves to `-2`);
  // the other entries keep their relative order, so chapter numbering is
  // unchanged.
  const extraKeys = new Set(extras.map((e) => e.key))
  const { anchorById, idByAnchor, extraAnchorsById } = resolveSectionAnchors([
    ...extras.map((e) => ({ id: e.key, base: e.anchor, explicit: [e.anchor] })),
    ...tocEntries
      .filter((e) => !extraKeys.has(e.id))
      .map((e) => {
        const s = data.sections.find((x) => x.id === e.id)
        return {
          id: e.id,
          // The synthetic Overview entry keeps its English slug (#overview) so
          // deep links stay stable across locales; its LABEL is translated.
          base: (e.id === OVERVIEW_ID ? 'overview' : slugifyAnchor(e.label)) || 'section',
          explicit: s && s.kind === 'chapter' ? s.anchorIds : undefined,
        }
      }),
  ])

  // Mobile-only drawer (right-edge pull-out) that mirrors the desktop
  // left-rail (TOC) and right-rail (Tools / Safety / Rope / Prerequisites)
  // content. Below `lg` the rails are hidden, so this drawer is the
  // only way to reach them.
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Desktop-only collapse for the two side rails. Each remembers its own
  // choice (localStorage) and starts collapsed on narrow-desktop widths
  // (< 1280px) so tablet-landscape / small laptops get a full-width article
  // by default — see useCollapsibleRail.
  const { collapsed: tocCollapsed, toggle: toggleToc } = useCollapsibleRail(
    railKeys.toc,
    { initialCollapsed: tocInitialCollapsed },
  )
  const { collapsed: toolsCollapsed, toggle: toggleTools } = useCollapsibleRail(
    railKeys.tools,
    { initialCollapsed: toolsInitialCollapsed },
  )

  // Featured image full-screen viewer (plan C1). For scenes whose only
  // Scene-tab photo was the cover (D4) this is the only place that photo
  // appears, so it must be openable at full size.
  const [featuredOpen, setFeaturedOpen] = useState(false)
  const featuredAlt = data.coverImageAlt || data.title
  const hasCreditsBlock = hasCredits(data.credits)
  const creditsLabel = data.creditsLabel || t('articleView.sceneCredits')
  const creditLabels = {
    rigger: t('articleView.creditRigger'),
    model: t('articleView.creditModel'),
    photographer: t('articleView.creditPhotographer'),
    location: t('articleView.creditLocation'),
  }

  // Body-scroll lock while the drawer is open — prevents the page
  // underneath from scrolling when the user drags inside the drawer.
  useEffect(() => {
    if (!drawerOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [drawerOpen])

  // Scroll-spy + reading-progress, one listener for both. The
  // synthetic Overview anchor is checked alongside the real sections,
  // plus the mobile-only auxiliary anchors (safety / rope / prereq).
  useEffect(() => {
    const ids = [
      OVERVIEW_ID,
      AUX_SAFETY_ID,
      AUX_ROPE_ID,
      AUX_CREDITS_ID,
      AUX_PREREQ_ID,
      // Rendered sections in page order, extra sections included (PR-2c).
      ...renderedSections.map((entry) =>
        entry.type === 'section' ? entry.section.id : entry.extra.key,
      ),
    ]
    const onScroll = () => {
      const sightline = window.innerHeight * 0.3
      let current = ids[0]
      for (const id of ids) {
        const el = sectionRefs.current[id]
        if (!el) continue
        const rect = el.getBoundingClientRect()
        // Skip elements that aren't laid out (display:none — e.g. the
        // mobile-only aux sections on a desktop viewport). They'd
        // otherwise report top=0 and always win the scroll-spy.
        if (rect.width === 0 && rect.height === 0) continue
        if (rect.top <= sightline) current = id
      }
      setActiveSection(current)

      // Reading progress reaches 100% when the reader reaches the COMMENTS —
      // the discussion below isn't part of "reading" the tutorial, so it
      // shouldn't count toward the total. Cap the scrollable range at the top
      // of the comments section; fall back to the full document height when
      // comments are absent (disabled, or gated → no visible sections).
      const doc = document.documentElement
      const commentsId = visibleSections.find((s) => s.kind === 'comments')?.id
      const commentsEl = commentsId ? sectionRefs.current[commentsId] : null
      const contentEnd = commentsEl
        ? commentsEl.getBoundingClientRect().top + doc.scrollTop
        : doc.scrollHeight
      const scrollable = contentEnd - doc.clientHeight
      setProgress(
        scrollable > 0 ? Math.min(100, Math.round((doc.scrollTop / scrollable) * 100)) : 0,
      )
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [visibleSections, renderedSections])

  // Deep-link + in-page anchor links: jump to the chapter named in the URL
  // hash (e.g. /tutorials/foo#core-technique). Resolved case-insensitively so
  // legacy WordPress anchors — which kept the heading's original
  // capitalization — still reach the lowercase slug the chapter renders now.
  // Runs on first load AND on hashchange, because an in-body `<a href="#Cap…">`
  // whose exact id doesn't exist gets no native scroll: we complete the jump
  // here. First-load jump is deferred a tick so section refs are registered.
  useEffect(() => {
    const jump = (smooth: boolean) => {
      const raw = decodeURIComponent(window.location.hash.slice(1))
      if (!raw) return
      const id = idByAnchor[raw] ?? idByAnchor[raw.toLowerCase()]
      const target =
        (id ? sectionRefs.current[id] : null) ??
        findElementByCaseInsensitiveId(raw) ??
        (gated ? paywallRef.current : null)
      if (target) {
        window.scrollTo({
          top: target.getBoundingClientRect().top + window.scrollY - 96,
          behavior: smooth ? 'smooth' : 'auto',
        })
      }
    }
    const t = window.setTimeout(() => jump(false), 50)
    const onHash = () => jump(true)
    window.addEventListener('hashchange', onHash)
    return () => {
      window.clearTimeout(t)
      window.removeEventListener('hashchange', onHash)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Step progress Resume scrolls to a chapter through the section refs (no
  // URL hash change, unlike a TOC click).
  const getChapterElement = useCallback(
    (id: string) => sectionRefs.current[id] ?? null,
    [],
  )

  const scrollToSection = (id: string) => {
    // Section refs only exist for the sections we actually rendered.
    // Gated entries (and the aux Safety/Rope/Prereq entries on a
    // gated tutorial) fall back to the paywall block, so the click
    // still goes somewhere useful instead of scrolling nowhere.
    const target =
      sectionRefs.current[id] ?? (gated ? paywallRef.current : null)
    if (target) {
      const y = target.getBoundingClientRect().top + window.scrollY - 96
      window.scrollTo({ top: y, behavior: 'smooth' })
    }
    // Reflect the chapter in the URL (shareable / bookmarkable) without the
    // browser's native instant jump — we already smooth-scrolled above.
    const anchor = anchorById[id]
    if (anchor) window.history.replaceState(null, '', `#${anchor}`)
    // If the user tapped a TOC entry inside the mobile drawer, close
    // it on the way out so the section they navigated to is visible.
    setDrawerOpen(false)
  }

  // Map the raw schema value (`beginner` | `intermediate` | `advanced`)
  // to its localized label — the same `filters.*` keys the grid chips use
  // (`beginner` → "Easy"). An unknown value still falls back to the
  // capitalized raw string rather than a missing-message key.
  const difficultyLabel = data.difficulty
    ? data.difficulty === 'beginner' ||
      data.difficulty === 'intermediate' ||
      data.difficulty === 'advanced'
      ? t(`filters.${data.difficulty}`)
      : data.difficulty.charAt(0).toUpperCase() + data.difficulty.slice(1)
    : null

  // ─── Rail render helpers ──────────────────────────────────────
  // Both the desktop rails and the mobile drawer render the same
  // content. Defining the markup inline as functions keeps the JSX
  // in one place and lets it close over component state.

  const renderTocRail = ({
    showProgress = true,
    includeAux = false,
  }: { showProgress?: boolean; includeAux?: boolean } = {}) => {
    // On mobile the rails collapse, so the drawer's TOC also lists
    // the auxiliary blocks (safety / rope / prerequisites) that live
    // in the right rail on desktop. They get spliced in right after
    // Overview so they read as "before you start" reference info.
    const auxEntries: TocEntry[] = []
    // Drawer (mobile) TOC always lists the aux blocks when they have
    // content, even on gated tutorials — same "breadth-of-content"
    // principle as the section list. Tapping a locked entry scrolls
    // to the paywall via the scrollToSection fallback.
    if (includeAux) {
      if (data.safetyItems.length > 0) {
        auxEntries.push({ id: AUX_SAFETY_ID, label: t('articleView.safety'), count: null, iconKind: 'text' })
      }
      if (data.ropeNeeded.length > 0) {
        auxEntries.push({ id: AUX_ROPE_ID, label: t('articleView.ropeNeeded'), count: null, iconKind: 'text' })
      }
      if (hasCreditsBlock) {
        auxEntries.push({ id: AUX_CREDITS_ID, label: creditsLabel, count: null, iconKind: 'text' })
      }
      if (data.prerequisites.length > 0) {
        auxEntries.push({ id: AUX_PREREQ_ID, label: t('articleView.prerequisites'), count: null, iconKind: 'text' })
      }
    }
    // When gated, the aux ids also count as "locked" — the same
    // scroll-to-paywall fallback applies.
    const lockedIds = new Set<string>(lockedSectionIds)
    if (gated) {
      lockedIds.add(AUX_SAFETY_ID)
      lockedIds.add(AUX_ROPE_ID)
      lockedIds.add(AUX_PREREQ_ID)
    }
    const entries =
      auxEntries.length > 0
        ? [tocEntries[0], ...auxEntries, ...tocEntries.slice(1)]
        : tocEntries

    return (
      <div>
        <div className="mb-4 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {t('articleView.onThisPage')}
        </div>
        <nav className="space-y-0.5">
          {entries.map((entry, i) => {
            const isActive = activeSection === entry.id
            const isLocked = lockedIds.has(entry.id)
            // Section-type icon (video → camera, photos → images, text →
            // align-left). A locked entry shows the Lock instead.
            const TypeIcon = TOC_ICONS[entry.iconKind]
            const button = (
              <button
                type="button"
                onClick={() => scrollToSection(entry.id)}
                aria-label={isLocked ? t('articleView.lockedEntry', { label: entry.label }) : entry.label}
                className="flex w-full items-baseline gap-2.5 py-1.5 text-left transition-colors"
                style={
                  isActive
                    ? { background: 'color-mix(in srgb, var(--rope) 18%, transparent)' }
                    : undefined
                }
              >
                <span
                  className="flex flex-1 items-baseline gap-1.5 pl-2 pr-2 text-[13px] leading-snug"
                  style={{
                    color: isActive
                      ? 'var(--rope-deep)'
                      : isLocked
                        ? 'var(--muted-foreground)'
                        : 'var(--foreground)',
                    fontWeight: isActive ? 600 : 400,
                  }}
                >
                  {isLocked ? (
                    <Lock
                      className="h-3 w-3 shrink-0 translate-y-[1px]"
                      aria-hidden
                    />
                  ) : (
                    <TypeIcon
                      className="h-3 w-3 shrink-0 translate-y-[1px] text-muted-foreground"
                      aria-hidden
                    />
                  )}
                  {entry.label}
                </span>
                {entry.count != null && (
                  <span className="pr-2 font-mono text-[11px] tabular-nums text-muted-foreground">
                    {entry.count}
                  </span>
                )}
              </button>
            )
            // Visual breaks in the list:
            //   · after the aux block (separates the "before you
            //     start" group from the article's content sections)
            //   · before What's next (same separator the section uses)
            const isFirstAfterAux =
              auxEntries.length > 0 && i === auxEntries.length + 1
            const needsDivider = isFirstAfterAux || entry.id === '__whats-next'
            if (needsDivider) {
              return (
                <Fragment key={entry.id}>
                  <div className="my-1.5 border-t border-border" />
                  {button}
                </Fragment>
              )
            }
            return <Fragment key={entry.id}>{button}</Fragment>
          })}
        </nav>

        {/* Reading progress — desktop only; the drawer omits it. */}
        {showProgress && (
          <div className="mt-6 border-t border-border pt-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                <Clock className="h-3 w-3" aria-hidden />
                {t('articleView.readingProgress')}
              </span>
              <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
                {progress}%
              </span>
            </div>
            <div className="h-[3px] w-full overflow-hidden bg-border">
              <div
                className="h-full transition-[width] duration-150"
                style={{ width: `${progress}%`, background: 'var(--rope-deep)' }}
              />
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderToolsRail = () => (
    <div className="space-y-7">
      {/* Tools */}
      <div>
        <RailHeading>{t('articleView.tools')}</RailHeading>
        <ToolsRow
          contentId={data.contentId}
          userId={data.userId}
          shareUrl={data.shareUrl}
          shareTitle={data.title}
        />
      </div>

      {/* Safety warning — the tutorial's safety_notice items. When
          gated the block stays in the layout (so users see the
          breadth of locked content) but its body collapses into a
          compact Members Only paywall. */}
      {data.safetyItems.length > 0 && (
        <div>
          <div
            className="mb-3 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em]"
            style={{ color: 'var(--ink)' }}
          >
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {t('articleView.safety')}
          </div>
          {gated ? (
            <RailPaywall
              onUnlock={() =>
                paywallRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
            />
          ) : (
          <div
            className="border p-4"
            style={{
              borderColor: 'var(--rope-deep)',
              background: 'color-mix(in srgb, var(--rope) 10%, var(--card))',
            }}
          >
            {data.safetyItems.map((item, i) => (
              <div key={i} className={i > 0 ? 'mt-3 border-t border-dotted border-border pt-3' : ''}>
                {item.title && (
                  <div className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>
                    {item.title}
                  </div>
                )}
                {item.body.length > 0 && (
                  <div className="prose prose-sm mt-0.5 max-w-none [&_p]:my-0 [&_p]:text-[13px] [&_p]:leading-relaxed [&_p]:text-muted-foreground [&_p:not(:first-child)]:mt-2 [&_li]:text-[13px] [&_li]:leading-relaxed [&_li]:text-muted-foreground">
                    <PortableTextRenderer value={item.body as never} />
                  </div>
                )}
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {/* You'll need — rope_needed rich text. Paywalled when gated. */}
      {data.ropeNeeded.length > 0 && (
        <div>
          <RailHeading>{t('articleView.ropeNeeded')}</RailHeading>
          {gated ? (
            <RailPaywall
              onUnlock={() =>
                paywallRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
            />
          ) : (
          <div className="border border-border bg-card px-4 py-3">
            <div className="prose prose-sm max-w-none [&_p]:text-[13px] [&_p]:leading-relaxed [&_li]:text-[13px] [&_li]:leading-relaxed">
              <PortableTextRenderer value={data.ropeNeeded as never} />
            </div>
          </div>
          )}
        </div>
      )}

      {/* Credits (scenes + tutorials) — public attribution, shown to every
          viewer including locked ones (plan D8), so never paywalled. */}
      {hasCreditsBlock && (
        <div>
          <RailHeading>{creditsLabel}</RailHeading>
          <CreditsBlock credits={data.credits!} labels={creditLabels} />
        </div>
      )}

      {/* Prerequisites — paywalled when gated. */}
      {data.prerequisites.length > 0 && (
        <div>
          <RailHeading>{t('articleView.prerequisites')}</RailHeading>
          {gated ? (
            <RailPaywall
              onUnlock={() =>
                paywallRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
            />
          ) : (
          <div className="space-y-1.5">
            {data.prerequisites.map((p) => (
              <ContentRefListItem key={p.id} href={p.href} title={p.title} thumbnailUrl={p.thumbnailUrl} />
            ))}
          </div>
          )}
        </div>
      )}

      {/* Related Techniques — same card layout as Prerequisites, but
          never paywalled: these are "see also" navigation aids, not
          gated knowledge required before the current tutorial. */}
      {data.relatedTechniques.length > 0 && (
        <div>
          <RailHeading>{t('articleView.moreLikeThis')}</RailHeading>
          <div className="space-y-1.5">
            {data.relatedTechniques.map((r) => (
              <ContentRefListItem
                key={r.id}
                href={`/tutorials/${r.slug}`}
                title={r.title}
                thumbnailUrl={r.thumbnailUrl}
              />
            ))}
          </div>
        </div>
      )}

    </div>
  )

  // Grid template tracks the two collapsible rails: a collapsed rail drops
  // its fixed-px track so the center `minmax(0,1fr)` reclaims the width. The
  // asides stay in the DOM (display:none via `hidden`), which grid skips, so
  // the remaining items map onto the remaining tracks in order.
  const railGridCols = tocCollapsed
    ? toolsCollapsed
      ? 'lg:grid-cols-[minmax(0,1fr)]'
      : 'lg:grid-cols-[minmax(0,1fr)_300px]'
    : toolsCollapsed
      ? 'lg:grid-cols-[230px_minmax(0,1fr)]'
      : 'lg:grid-cols-[230px_minmax(0,1fr)_300px]'

  return (
    <ImageLayoutContext.Provider value={imageLayoutConfig}>
    <StepViewProvider
      initialMode={stepViewDefault}
      authed={stepViewAuthed}
      defaultMode={stepViewDefaultMode}
      options={stepViewOptions}
    >
    <StepProgressProvider
      docId={stepProgressDocId}
      tracking={stepTracking}
      onTrackingChange={setStepTracking}
      chapters={progressChapters}
      getChapterElement={getChapterElement}
    >
    <div className="bg-white dark:bg-background">
      {/* Asymmetric horizontal padding on mobile (pl-4 / pr-14) keeps
          every piece of content — title, cover image, sections,
          comments — out from under the right-edge drawer tab. Desktop
          reverts to symmetric padding via `lg:px-8`. */}
      <div className={`mx-auto grid max-w-[1500px] grid-cols-1 gap-10 py-10 pl-4 pr-8 ${railGridCols} lg:px-8`}>
        {/* ─── LEFT RAIL · table of contents (desktop only) ─ */}
        <aside className={tocCollapsed ? 'hidden' : 'hidden lg:block'}>
          <div className="scrollbar-thin sticky top-24 max-h-[calc(100vh-7rem)] space-y-4 overflow-y-auto">
            {/* Collapse control — hands the rail's 230px to the article. */}
            <div className="flex justify-end">
              <RailCollapseButton side="left" label={t('articleView.contents')} onClick={toggleToc} />
            </div>
            {/* Featured image — pinned above the table of contents on
                desktop. On mobile (or with this rail collapsed) the same
                image renders inline in the article header instead (below).
                Loads eagerly (it's above the fold whenever the rail shows)
                and opens full-screen (plan C1). */}
            {data.coverImageUrl && (
              <div className="overflow-hidden border border-border">
                <FeaturedImageButton onOpen={() => setFeaturedOpen(true)} label={t('articleView.viewFeaturedFullScreen')}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={optimizeSanityImageUrl(data.coverImageUrl, 600)}
                    alt={featuredAlt}
                    loading="eager"
                    decoding="async"
                    className="block h-auto w-full"
                  />
                </FeaturedImageButton>
              </div>
            )}
            {renderTocRail({ showProgress: !gated })}
          </div>
        </aside>

        {/* ─── CENTER · article body ──────────────────────── */}
        {/* Content column — a <div>, not a nested <main>: the (public) layout
            already provides the page's single <main id="main-content"> landmark,
            and two <main>s on a page is a WCAG 1.3.1 violation. */}
        <div className="min-w-0">
          {/* Overview — title + difficulty + full description. Carries
              the synthetic OVERVIEW_ID anchor so the TOC can scroll
              back up to it. */}
          <header
            ref={(el: HTMLElement | null) => {
              sectionRefs.current[OVERVIEW_ID] = el
            }}
            className="scroll-mt-24"
          >
            {/* Eyebrow above the title (flogger breadcrumb, PR-3). */}
            {headerEyebrow ? <div className="mb-3">{headerEyebrow}</div> : null}
            {/* Title */}
            <h1
              className="text-3xl leading-[1.05] tracking-tight sm:text-4xl"
              style={{ fontFamily: HEADING_FONT, color: 'var(--ink)' }}
            >
              {data.title}
            </h1>

            {authorDisplayName(data.author) && (
              <p className="mt-3 text-sm text-muted-foreground">
                {t('blogPost.byAuthor', { author: authorDisplayName(data.author) ?? '' })}
              </p>
            )}
            {/* Header metadata under the byline (flogger published date, PR-3). */}
            {headerMeta ? (
              <div className={`${authorDisplayName(data.author) ? 'mt-1' : 'mt-3'} text-sm text-muted-foreground`}>
                {headerMeta}
              </div>
            ) : null}

            {/* Difficulty + (when relevant) Members Only chips. */}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {difficultyLabel && (
                <span
                  className="inline-block font-mono text-[10px] uppercase tracking-[0.18em] px-2.5 py-1"
                  style={{
                    background: 'color-mix(in srgb, var(--rope) 16%, transparent)',
                    color: 'var(--rope-deep)',
                  }}
                >
                  {difficultyLabel}
                </span>
              )}
              {data.isGated && (
                <span
                  className="inline-block font-mono text-[10px] uppercase tracking-[0.18em] px-2.5 py-1"
                  style={{
                    background: 'color-mix(in srgb, var(--brand-500) 14%, transparent)',
                    color: 'var(--brand-700)',
                  }}
                >
                  {t('filters.membersOnly')}
                </span>
              )}
              {/* Page header action (PR-2c; PR-3's Notation guide button).
                  Before Resume, which appears only after mount, so the action
                  never moves when Resume shows; the row wraps on narrow
                  screens and the action keeps its size (`shrink-0`) within
                  the column (`max-w-full`). */}
              {headerAction ? (
                <div className="flex max-w-full shrink-0 items-center">{headerAction}</div>
              ) : null}
              {/* Step progress Resume (PR-2b): renders only with progress on
                  and at least one step done, after mount. */}
              <StepProgressResumeButton />
            </div>

            {/* Featured image — on desktop it lives in the left rail above
                the ToC (see the aside above); inline here only below `lg`.
                With the desktop rails collapsed it is NOT repeated above the
                overview (owner decision 2026-10-28). Lazy: hidden on desktop
                (display:none), so this copy never downloads there. */}
            {data.coverImageUrl && (
              <div className="mt-6 overflow-hidden border border-border lg:hidden">
                <FeaturedImageButton onOpen={() => setFeaturedOpen(true)} label={t('articleView.viewFeaturedFullScreen')}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={optimizeSanityImageUrl(data.coverImageUrl, 1400)}
                    alt={featuredAlt}
                    loading="lazy"
                    decoding="async"
                    className="block h-auto w-full"
                  />
                </FeaturedImageButton>
              </div>
            )}

            {/* Full description / body — the whole Portable Text block,
                rendered above the first section (the video). Falls back
                to the short excerpt only if the tutorial has no body.
                When gated and the viewer lacks access we always show the
                short excerpt as the "above the paywall" preview. */}
            {gated ? (
              data.excerpt && (
                <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
                  {data.excerpt}
                </p>
              )
            ) : data.body && data.body.length > 0 ? (
              <div className="mt-5">
                <PortableTextRenderer value={data.body} lang={data.bodyLang ?? null} />
              </div>
            ) : data.excerpt ? (
              <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
                {data.excerpt}
              </p>
            ) : null}
          </header>

          {/* ─── Paywall ─────────────────────────────────────────
              When the tutorial is gated and the viewer lacks access,
              the media + reference sections collapse into a single
              ContentGate block. Everything below (what's-next,
              comments) is still shown — those aren't gated. */}
          {gated && (
            <div ref={paywallRef} className="mt-10 scroll-mt-24">
              <ContentGate
                isGated
                hasAccess={false}
                isAuthenticated={data.isAuthenticated}
                excerpt={undefined}
                showFade={false}
                showEmailCapture={false}
              >
                {null}
              </ContentGate>
            </div>
          )}

          {/* ─── MOBILE-ONLY · pre-flight reference blocks ───
              These live in the desktop right rail; on mobile they get
              rolled into the page flow (and the drawer's TOC) so they
              aren't lost. Each carries its TOC anchor. When the
              tutorial is gated the heading + paywall placeholder still
              render so users see the breadth of locked content. */}
          {data.safetyItems.length > 0 && (
            <section
              ref={(el) => { sectionRefs.current[AUX_SAFETY_ID] = el }}
              className="mt-10 scroll-mt-24 lg:hidden"
            >
              <h2
                className="flex items-center gap-2 text-2xl tracking-tight"
                style={{ fontFamily: HEADING_FONT, color: 'var(--ink)' }}
              >
                <AlertTriangle
                  className="h-6 w-6 shrink-0"
                  style={{ color: 'var(--ink)' }}
                  aria-hidden
                />
                {t('articleView.safety')}
              </h2>
              {gated ? (
                <div className="mt-3">
                  <RailPaywall
                    onUnlock={() =>
                      paywallRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                  />
                </div>
              ) : (
              <div
                className="mt-3 border p-4"
                style={{
                  borderColor: 'var(--rope-deep)',
                  background: 'color-mix(in srgb, var(--rope) 10%, var(--card))',
                }}
              >
                {data.safetyItems.map((item, i) => (
                  <div
                    key={i}
                    className={i > 0 ? 'mt-3 border-t border-dotted border-border pt-3' : ''}
                  >
                    {item.title && (
                      <div className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>
                        {item.title}
                      </div>
                    )}
                    {item.body.length > 0 && (
                      <div className="prose prose-sm mt-0.5 max-w-none [&_p]:my-0 [&_p]:text-[13px] [&_p]:leading-relaxed [&_p]:text-muted-foreground [&_p:not(:first-child)]:mt-2 [&_li]:text-[13px] [&_li]:leading-relaxed [&_li]:text-muted-foreground">
                        <PortableTextRenderer value={item.body as never} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              )}
            </section>
          )}
          {data.ropeNeeded.length > 0 && (
            <section ref={(el) => { sectionRefs.current[AUX_ROPE_ID] = el }} className="mt-10 scroll-mt-24 lg:hidden">
              <h2
                className="text-2xl tracking-tight"
                style={{ fontFamily: HEADING_FONT, color: 'var(--ink)' }}
              >
                {t('articleView.ropeNeeded')}
              </h2>
              {gated ? (
                <div className="mt-3">
                  <RailPaywall
                    onUnlock={() =>
                      paywallRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                  />
                </div>
              ) : (
              <div className="mt-3 border border-border bg-card px-4 py-3">
                <div className="prose prose-sm max-w-none [&_p]:text-[13px] [&_p]:leading-relaxed [&_li]:text-[13px] [&_li]:leading-relaxed">
                  <PortableTextRenderer value={data.ropeNeeded as never} />
                </div>
              </div>
              )}
            </section>
          )}
          {hasCreditsBlock && (
            <section ref={(el) => { sectionRefs.current[AUX_CREDITS_ID] = el }} className="mt-10 scroll-mt-24 lg:hidden">
              <h2
                className="text-2xl tracking-tight"
                style={{ fontFamily: HEADING_FONT, color: 'var(--ink)' }}
              >
                {creditsLabel}
              </h2>
              <div className="mt-3 border border-border bg-card px-4 py-3">
                <CreditsBlock credits={data.credits!} labels={creditLabels} />
              </div>
            </section>
          )}
          {data.prerequisites.length > 0 && (
            <section ref={(el) => { sectionRefs.current[AUX_PREREQ_ID] = el }} className="mt-10 scroll-mt-24 lg:hidden">
              <h2
                className="text-2xl tracking-tight"
                style={{ fontFamily: HEADING_FONT, color: 'var(--ink)' }}
              >
                {t('articleView.prerequisites')}
              </h2>
              {gated ? (
                <div className="mt-3">
                  <RailPaywall
                    onUnlock={() =>
                      paywallRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                  />
                </div>
              ) : (
              <div className="mt-3 space-y-1.5">
                {data.prerequisites.map((p) => (
                  <ContentRefListItem key={p.id} href={p.href} title={p.title} thumbnailUrl={p.thumbnailUrl} size="md" />
                ))}
              </div>
              )}
            </section>
          )}

          {/* Related Techniques — mobile-only inline rendering. On
              desktop the same data renders in the right rail; this
              copy keeps small-viewport users from missing the see-
              also cards entirely. Never paywalled. */}
          {data.relatedTechniques.length > 0 && (
            <section className="mt-10 scroll-mt-24 lg:hidden">
              <h2
                className="text-2xl tracking-tight"
                style={{ fontFamily: HEADING_FONT, color: 'var(--ink)' }}
              >
                {t('articleView.moreLikeThis')}
              </h2>
              <div className="mt-3 space-y-1.5">
                {data.relatedTechniques.map((r) => (
                  <ContentRefListItem
                    key={r.id}
                    href={`/tutorials/${r.slug}`}
                    title={r.title}
                    thumbnailUrl={r.thumbnailUrl}
                    size="md"
                  />
                ))}
              </div>
            </section>
          )}

          {/* Sections (plus any extra sections, in page order) */}
          {renderedSections.map((entry) => {
            if (entry.type === 'extra') {
              // Extra (non-chapter) section (PR-2c): same shell, heading and
              // spacing as a chapter, but step progress is off inside it (no
              // checkbox, switch or count), since only chapters are tracked.
              const { key, extra } = entry.extra
              return (
                <SectionShell
                  key={key}
                  ref={(el) => { sectionRefs.current[key] = el }}
                  label={extra.tocLabel}
                  anchor={anchorById[key]}
                >
                  <div className="mt-6">
                    <StepProgressOff>{extra.content}</StepProgressOff>
                  </div>
                </SectionShell>
              )
            }
            const section = entry.section
            if (section.kind === 'chapter') {
              // A chapter is the new import unit (one per legacy accordion
              // section). Its title is the section heading; its content
              // (prose + step rows w/ lightbox + video + bonus photos w/
              // lightbox) renders through PortableTextRenderer, which owns
              // those block renders + their lightboxes. Goes through the
              // same SectionShell / gating / TOC machinery as every other
              // section kind.
              // While step progress is on (offered + "Track my steps"), the
              // heading gets an id so the step checkboxes can point
              // aria-describedby at it ("Mark step 1 done" in two chapters is
              // told apart). Off → no id, so the markup is unchanged.
              const chapterHeadingId = stepProgressOn
                ? `step-progress-chapter-${section.id}`
                : null
              return (
                <SectionShell
                  key={section.id}
                  ref={(el) => { sectionRefs.current[section.id] = el }}
                  label={section.label}
                  labelLang={section.labelLang}
                  anchor={anchorById[section.id]}
                  extraAnchors={extraAnchorsById[section.id]}
                  headingId={chapterHeadingId}
                >
                  {/* Per-chapter "x of n done" (PR-2b; nothing when off). */}
                  <ChapterStepProgress sectionId={section.id} />
                  <div className="mt-6">
                    <StepProgressChapter headingId={chapterHeadingId}>
                      <PortableTextRenderer
                        value={section.content as never}
                        lang={data.bodyLang ?? null}
                        enTextKeys={section.enTextKeys}
                      />
                    </StepProgressChapter>
                  </div>
                </SectionShell>
              )
            }
            if (section.kind === 'whatsNext') {
              return (
                <SectionShell
                  key={section.id}
                  ref={(el) => { sectionRefs.current[section.id] = el }}
                  label={section.label}
                  divider
                  anchor={anchorById[section.id]}
                >
                  {/* Fixed-size tiles (~200–240px) that add columns as the centre
                      column widens — with the rails collapsed, 2 fixed columns
                      blew each card up to ~500px squares. Phones keep 2. */}
                  <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-[repeat(auto-fill,minmax(200px,1fr))]">
                    {section.items.map((item) => (
                      <LinkedCardTile
                        key={item.id}
                        item={item}
                        typeLabel={item.typeKey ? t(`cards.chip.${item.typeKey}`) : null}
                      />
                    ))}
                  </div>
                </SectionShell>
              )
            }
            if (section.kind === 'comments') {
              if (data.commentsEnabled === false) return null
              // No SectionShell heading here — the Comments component
              // brings its own "Comments (N)" header + separator bar,
              // so a numbered section heading would just duplicate it.
              return (
                <section key={section.id} id={anchorById[section.id]} ref={(el) => { sectionRefs.current[section.id] = el }} className="scroll-mt-24">
                  <Comments contentId={data.contentId} userId={data.userId} />
                </section>
              )
            }
            return null
          })}
        </div>

        {/* ─── RIGHT RAIL · tools (desktop only) ──────────── */}
        <aside className={toolsCollapsed ? 'hidden' : 'hidden lg:block'}>
          {/* Flows with the article rather than sticky-pinned. The tools rail
              (Tools, Risk/Safety, Rope Needed, Prerequisites, More-Like-This)
              routinely runs taller than the viewport; a `sticky top-24
              max-h-[calc(100vh-7rem)] overflow-y-auto` wrapper capped it to
              the viewport and diverted the overflow into a 4px hairline
              scrollbar that page-scroll never advanced, so the lower sections
              (rope needed / prerequisites) were unreachable by normal
              scrolling. Matching the scene page's Spec sidebar, a plain flow
              column keeps every section reachable. */}
          <div>
            {/* Collapse control — hands the rail's 300px to the article. */}
            <div className="mb-3 flex justify-start">
              <RailCollapseButton side="right" label={t('articleView.tools')} onClick={toggleTools} />
            </div>
            {renderToolsRail()}
          </div>
        </aside>
      </div>

      {/* Desktop re-open tabs — shown only while a rail is collapsed, docked
          to the matching edge (mirrors the mobile drawer tab idiom). Below
          `lg` they're hidden; the mobile drawer is the affordance there. */}
      {tocCollapsed && (
        <RailReopenHandle side="left" label={t('articleView.contents')} onClick={toggleToc} />
      )}
      {toolsCollapsed && (
        <RailReopenHandle side="right" label={t('articleView.tools')} onClick={toggleTools} />
      )}

      {/* ─── MOBILE · pull-out drawer ─────────────────────────────
          On phones + small tablets the desktop rails collapse out
          of the layout, so the TOC + Tools/Safety/Rope/Prerequisites
          would be unreachable. This drawer brings them back: a small
          tab docked to the right edge opens a right-aligned panel
          with the same content. */}

      {/* Trigger — fixed pull-tab on the right edge, anchored near the
          page title so it lives in the same eyeline as the headline
          instead of floating in the middle of the viewport. When the
          drawer opens, the tab slides left in lockstep with the panel
          so it stays docked to the panel's left edge (rather than
          floating over the panel's right edge) — same control, just
          riding the drawer in and out. Translation matches the panel
          width exactly: `min(88vw, 24rem)` mirrors `w-[88%] max-w-sm`. */}
      <button
        type="button"
        onClick={() => setDrawerOpen((v) => !v)}
        aria-label={drawerOpen ? t('articleView.closeDrawer') : t('articleView.openDrawer')}
        aria-expanded={drawerOpen}
        aria-controls="tutorial-tools-drawer"
        className="fixed right-0 top-20 z-50 flex flex-col items-center gap-1 rounded-l-md px-1.5 py-3 text-white shadow-lg transition-transform duration-300 ease-out lg:hidden"
        style={{
          background: 'var(--rope-deep)',
          transform: drawerOpen
            ? 'translateX(calc(-1 * min(88vw, 24rem)))'
            : 'translateX(0)',
        }}
      >
        {drawerOpen ? (
          <X className="h-4 w-4" aria-hidden />
        ) : (
          <PanelRightOpen className="h-4 w-4" aria-hidden />
        )}
        <span className="font-mono text-[9px] uppercase tracking-[0.14em] [writing-mode:vertical-rl] [text-orientation:mixed]">
          {drawerOpen ? t('articleView.close') : t('articleView.onThisPage')}
        </span>
      </button>

      {/* Drawer + backdrop. Rendered always (so the slide-in / slide-out
          transition runs both ways), positioned off-screen when closed. */}
      <div
        className={`fixed inset-0 z-40 lg:hidden ${
          drawerOpen ? '' : 'pointer-events-none'
        }`}
        aria-hidden={!drawerOpen}
      >
        {/* Backdrop */}
        <div
          onClick={() => setDrawerOpen(false)}
          className={`absolute inset-0 bg-black/40 transition-opacity duration-200 ${
            drawerOpen ? 'opacity-100' : 'opacity-0'
          }`}
        />
        {/* Panel — slides in from the right. Starts at `top-16` so its
            content clears the sticky site header (`h-16 z-50`); without
            this the top of the panel — and the Tools strip — sits
            behind the header bar. */}
        <aside
          id="tutorial-tools-drawer"
          role="dialog"
          aria-modal="true"
          aria-label={t('articleView.drawerLabel')}
          className={`absolute right-0 top-16 bottom-0 flex w-[88%] max-w-sm flex-col bg-background shadow-2xl transition-transform duration-300 ease-out ${
            drawerOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Body — Tools strip on top (no heading, tight spacing),
              then the TOC (which on mobile has Safety / Rope /
              Prerequisites rolled into it as entries pointing at the
              inline mobile sections in the article). Reading progress
              is omitted here; it stays on desktop. The right-edge tab
              is the close affordance, so no in-drawer header. */}
          <div className="flex-1 overflow-y-auto px-4 py-4">
            <ToolsRow
              contentId={data.contentId}
              userId={data.userId}
              shareUrl={data.shareUrl}
              shareTitle={data.title}
            />
            <div className="mt-4 border-t border-border pt-4">
              {renderTocRail({ showProgress: false, includeAux: true })}
            </div>
          </div>
        </aside>
      </div>

      {/* Featured image full-screen viewer (both copies open it). */}
      {data.coverImageUrl && (
        <Lightbox
          images={[{ src: optimizeSanityImageUrl(data.coverImageUrl, 2400), alt: featuredAlt }]}
          open={featuredOpen}
          onClose={() => setFeaturedOpen(false)}
        />
      )}
    </div>
    </StepProgressProvider>
    </StepViewProvider>
    </ImageLayoutContext.Provider>
  )
}

// ─── Tools row · Bookmark / Like / Share ─────────────────────────
//
// Mirrors the engagement logic in <LikeBookmarkButtons /> but renders
// in the concept design's 3-button tile layout. We accept `contentId`
// pre-resolved by the server (vs. the legacy component which looks it
// up from (type, slug) on mount) so the toggle works on the first
// click without waiting on a fetch.


// ─── Small helpers ───────────────────────────────────────────────

// A content tile — thumbnail + title — used by the Related techniques
// and What's next sections. Renders as a link when the target has a
// resolvable href, otherwise as a plain (non-interactive) card.
function LinkedCardTile({ item, typeLabel }: { item: LinkedCard; typeLabel: string | null }) {
  const inner = (
    <>
      <div className="relative">
        {item.thumbnailUrl ? (
          <SquareBlurImage
            src={item.thumbnailUrl}
            alt=""
            className="border border-border"
            imgClassName="transition-transform group-hover:scale-[1.03]"
            width={480}
          />
        ) : (
          <div
            className="aspect-square border border-border"
            style={{ background: 'color-mix(in srgb, var(--rope-light) 22%, var(--card))' }}
          />
        )}
        {/* Content-type chip — lower-right of the thumbnail. */}
        {typeLabel && (
          <span
            className="absolute bottom-2 right-2 z-20 bg-card px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider"
            style={{ color: 'var(--rope-deep)' }}
          >
            {typeLabel}
          </span>
        )}
      </div>
      <div className="mt-2 text-sm font-medium leading-snug" style={{ color: 'var(--ink)' }}>
        {item.title}
      </div>
    </>
  )
  if (item.href) {
    return (
      <Link href={item.href} className="group block">
        {inner}
      </Link>
    )
  }
  return <div className="group block">{inner}</div>
}

// Wraps a featured-image copy so it opens the full-screen viewer. A real
// <button> (keyboard + screen-reader operable); zoom cursor hints the action.
function FeaturedImageButton({
  onOpen,
  label,
  children,
}: {
  onOpen: () => void
  /** Localized accessible name (articleView.viewFeaturedFullScreen). */
  label: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={label}
      className="block w-full cursor-zoom-in"
    >
      {children}
    </button>
  )
}

function RailHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
      {children}
    </div>
  )
}

// Compact "Members Only" placeholder used in place of a rail block's
// real content when the tutorial is gated. Tapping it (or the unlock
// link) jumps the page to the main paywall card so the user can act.
function RailPaywall({ onUnlock }: { onUnlock?: () => void }) {
  const t = useTranslations()
  return (
    <button
      type="button"
      onClick={onUnlock}
      className="flex w-full flex-col items-start gap-1.5 border border-border bg-card p-4 text-left transition-colors hover:border-rope"
    >
      <span
        className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em]"
        style={{ color: 'var(--rope-deep)' }}
      >
        <Lock className="h-3 w-3" aria-hidden />
        {t('filters.membersOnly')}
      </span>
      <span className="text-[12px] text-muted-foreground">
        {t('cards.unlockHint')}
      </span>
    </button>
  )
}


const SectionShell = forwardRef<
  HTMLElement,
  {
    label: string
    /** BCP-47 lang for the heading when it differs from the page's. */
    labelLang?: string
    /** Optional rich-text intro. Accepts a Portable Text array so
     *  editor-authored headings, bullet lists, bold, and links all
     *  render — earlier this was a plain string flattened by
     *  ptToText() which silently dropped formatting. */
    intro?: SectionIntro
    /** Optional rich-text outro shown BELOW children. Currently used
     *  by the video section for "what I'm doing at each timestamp"
     *  numbered recaps the editor authored AFTER the video embed.
     *  Same Portable Text type as intro so it renders identically. */
    outro?: SectionIntro
    firstSection?: boolean
    /** Render a separator bar above the section (used for What's next,
     *  to match the divider the Comments section carries). */
    divider?: boolean
    /** URL hash anchor — sets the section's `id` so #anchor deep-links here. */
    anchor?: string
    /** Extra `#fragment` ids this section is also a target for (legacy
     *  WordPress anchors). Rendered as empty scroll-margin anchors so the
     *  section's primary `id` (the title slug) is preserved. */
    extraAnchors?: string[]
    /** Optional `id` for the `<h2>` (step progress describes its checkboxes
     *  by it). Omitted → no id attribute, as before. */
    headingId?: string | null
    children: React.ReactNode
  }
>(function SectionShell({ label, labelLang, intro, outro, firstSection, divider, anchor, extraAnchors, headingId, children }, ref) {
  const spacing = divider
    ? 'mt-12 border-t border-border pt-8'
    : firstSection
      ? 'mt-8'
      : 'mt-14'
  // Treat an empty array as "no intro" so we don't render a stray
  // gap when the Portable Text projection returned [].
  const hasIntro = Array.isArray(intro) && intro.length > 0
  const hasOutro = Array.isArray(outro) && outro.length > 0
  return (
    <section ref={ref} id={anchor} className={`${spacing} scroll-mt-24`}>
      {extraAnchors?.map((id) => (
        <span key={id} id={id} className="block scroll-mt-24" aria-hidden="true" />
      ))}
      <h2
        id={headingId ?? undefined}
        className="text-2xl tracking-tight"
        style={{ fontFamily: HEADING_FONT, color: 'var(--ink)' }}
        lang={labelLang}
      >
        {label}
      </h2>
      {hasIntro && (
        <div className="mt-3 max-w-2xl text-muted-foreground">
          <PortableTextRenderer value={intro!} />
        </div>
      )}
      {children}
      {hasOutro && (
        <div className="mt-4 max-w-2xl text-muted-foreground">
          <PortableTextRenderer value={outro!} />
        </div>
      )}
    </section>
  )
})
